import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline'
import Modal from '../ui/Modal'

type TipoAccion = 'eliminar' | 'cancelar' | 'confirmar' | 'advertencia' | 'info'

interface ModalConfirmacionProps {
  isOpen: boolean
  onClose: () => void
  onConfirmar: () => void
  titulo?: string
  title?: string
  mensaje?: string
  message?: string
  tipo?: TipoAccion
  type?: TipoAccion
  textoConfirmar?: string
  confirmText?: string
  textoCancelar?: string
  cancelText?: string
  requiereConfirmacion?: boolean
  textoConfirmacion?: string
  cargando?: boolean
  tipoBoton?: 'danger' | 'warning' | 'primary' | string
}

const ModalConfirmacion: React.FC<ModalConfirmacionProps> = ({
  isOpen,
  onClose,
  onConfirmar,
  titulo,
  mensaje,
  message,
  title,
  tipo = 'confirmar',
  type,
  textoConfirmar,
  confirmText,
  textoCancelar,
  cancelText,
  requiereConfirmacion = false,
  textoConfirmacion = '',
  cargando = false
}) => {
  const [confirmacionTexto, setConfirmacionTexto] = useState('')
  const tituloFinal = (titulo ?? title) ?? 'Confirmación'
  const mensajeFinal = (mensaje ?? message) ?? ''
  const tipoFinal: TipoAccion = (type ?? tipo) || 'confirmar'
  const textoConfirmarFinal = (textoConfirmar ?? confirmText) ?? 'Confirmar'
  const textoCancelarFinal = (textoCancelar ?? cancelText) ?? 'Cancelar'

  const configuracionesTipo = {
    eliminar: {
      icono: TrashIcon,
      colorIcono: 'text-red-600 dark:text-red-400',
      bgIcono: 'bg-red-100 dark:bg-red-900/30',
      colorBoton: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      gradiente: 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20'
    },
    cancelar: {
      icono: XCircleIcon,
      colorIcono: 'text-orange-600 dark:text-orange-400',
      bgIcono: 'bg-orange-100 dark:bg-orange-900/30',
      colorBoton: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      gradiente: 'from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20'
    },
    confirmar: {
      icono: CheckCircleIcon,
      colorIcono: 'text-green-600 dark:text-green-400',
      bgIcono: 'bg-green-100 dark:bg-green-900/30',
      colorBoton: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      gradiente: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
    },
    advertencia: {
      icono: ExclamationTriangleIcon,
      colorIcono: 'text-yellow-600 dark:text-yellow-400',
      bgIcono: 'bg-yellow-100 dark:bg-yellow-900/30',
      colorBoton: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700',
      gradiente: 'from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20'
    },
    info: {
      icono: InformationCircleIcon,
      colorIcono: 'text-blue-600 dark:text-blue-400',
      bgIcono: 'bg-blue-100 dark:bg-blue-900/30',
      colorBoton: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      gradiente: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
    }
  }

  const config = configuracionesTipo[tipoFinal]
  const IconoComponente = config.icono

  const puedeConfirmar = !requiereConfirmacion || 
    (requiereConfirmacion && confirmacionTexto.toLowerCase() === textoConfirmacion.toLowerCase())

  const handleConfirmar = () => {
    if (puedeConfirmar) {
      onConfirmar()
    }
  }

  const handleCerrar = () => {
    setConfirmacionTexto('')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCerrar}
      title={tituloFinal}
      size="md"
      showCloseButton={false}
    >
      <div className="space-y-6">
        {/* Contenido Principal */}
        <div className={`bg-gradient-to-r ${config.gradiente} rounded-xl p-6`}>
          <div className="flex items-start space-x-4">
            {/* Icono */}
            <div className={`flex-shrink-0 p-3 ${config.bgIcono} rounded-full`}>
              <IconoComponente className={`w-6 h-6 ${config.colorIcono}`} />
            </div>

            {/* Contenido */}
            <div className="flex-1">
              <div className="text-gray-900 dark:text-white leading-relaxed">
                {mensajeFinal}
              </div>

              {/* Campo de confirmación si es requerido */}
              {requiereConfirmacion && (
                <div className="mt-4">
                  <label htmlFor="modal-confirmacion-texto" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Para confirmar, escribe: <span className="font-bold text-red-600 dark:text-red-400">
                      {textoConfirmacion}
                    </span>
                  </label>
                  <input
                    id="modal-confirmacion-texto"
                    name="confirmacionTexto"
                    type="text"
                    value={confirmacionTexto}
                    onChange={(e) => setConfirmacionTexto(e.target.value)}
                    aria-invalid={Boolean(confirmacionTexto) && !puedeConfirmar}
                    aria-describedby={!puedeConfirmar && confirmacionTexto ? 'modal-confirmacion-texto-error' : undefined}
                    placeholder={`Escribe "${textoConfirmacion}" para confirmar`}
                    className={`
                      w-full px-3 py-2 border rounded-lg
                      focus:ring-2 focus:ring-red-500 focus:border-red-500
                      dark:bg-gray-700 dark:border-gray-600 dark:text-white
                      ${puedeConfirmar 
                        ? 'border-green-300 bg-green-50 dark:bg-green-900/20' 
                        : 'border-gray-300'
                      }
                    `}
                  />
                  
                  {confirmacionTexto && !puedeConfirmar && (
                    <p id="modal-confirmacion-texto-error" role="alert" className="text-red-500 text-sm mt-1">
                      El texto no coincide. Debe escribir exactamente: "{textoConfirmacion}"
                    </p>
                  )}
                  
                  {puedeConfirmar && (
                    <div className="flex items-center space-x-2 mt-2">
                      <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      <p className="text-green-600 text-sm">
                        Confirmación correcta
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Advertencia adicional para acciones destructivas */}
        {(tipoFinal === 'eliminar' || tipoFinal === 'cancelar') && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <ShieldExclamationIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                Esta acción no se puede deshacer
              </p>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {tipoFinal === 'eliminar' 
                ? 'Una vez eliminado, no podrás recuperar esta información.'
                : 'Una vez cancelado, el envío no podrá ser reactivado.'
              }
            </p>
          </div>
        )}

        {/* Botones de Acción */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCerrar}
            disabled={cargando}
            className="
              px-4 py-2 text-gray-700 dark:text-gray-300 
              border border-gray-300 dark:border-gray-600 
              rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors duration-200
            "
          >
            {textoCancelarFinal}
          </motion.button>
          
          <motion.button
            whileHover={puedeConfirmar && !cargando ? { scale: 1.02 } : {}}
            whileTap={puedeConfirmar && !cargando ? { scale: 0.98 } : {}}
            onClick={handleConfirmar}
            disabled={!puedeConfirmar || cargando}
            className={`
              px-6 py-2 text-white rounded-lg
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200 flex items-center space-x-2
              ${config.colorBoton}
            `}
          >
            {cargando ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <IconoComponente className="w-4 h-4" />
                <span>{textoConfirmarFinal}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Modal>
  )
}

export default ModalConfirmacion
